import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    roles?: string[]
    isLockout?: boolean
  }
}
import LoginPage from '@/pages/index.vue'
import AdminMasterLayout from "@/pages/app/administration/MasterLayout.vue";
import AdminDashboard from "@/pages/app/administration/AdminDashboard.vue";
import AdminTenantUsagePage from "@/pages/app/administration/tenant-metrics/page.vue"
import TenantAdministrationPage from "@/pages/app/administration/tenant-management/page.vue";
import { useUser } from './composables/useUser';
import { isLoggedIn } from './services/auth';
import { UserRoles } from './shared/models/UserRoles';
import { validateTokenClaims } from '@/services/auth/tokenValidator';


const routes: RouteRecordRaw[] = [
  { path: '/', component: LoginPage },
  {
    path: '/app/lockout',
    component: () => import('@/pages/app/lockout/LockoutScreen.vue'),
    meta: { requiresAuth: true, isLockout: true }
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
  {
    path: '/app',
    component: () => import('@/pages/app/layoutv2.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '/app',
        component: () => import('@/pages/app/dashboard/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/dashboard',
        component: () => import('@/pages/app/dashboard/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/uploads',
        component: () => import('@/pages/app/uploads/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/data-cleaning',
        component: () => import('@/pages/app/data-cleaning/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper] }
      },
      {
        path: '/app/analysis-config',
        component: () => import('@/pages/app/analysis-config/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper] }
      },
      {
        path: '/app/custom-calculations',
        component: () => import('@/pages/app/custom-calculations/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper] }
      },
      {
        path: '/app/plotting',
        component: () => import('@/pages/app/plotting/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper] }
      },
      {
        path: '/app/tables',
        component: () => import('@/pages/app/tables/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper] }
      },
      {
        path: '/app/custom-reports',
        component: () => import('@/pages/app/custom-reports/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper] }
      },
      {
        path: '/app/history',
        component: () => import('@/pages/app/history/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/validation-history',
        component: () => import('@/pages/app/nca-validation-history/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/nca-validation-history',
        component: () => import('@/pages/app/nca-validation-history/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/analysis',
        component: () => import('@/pages/app/analysis/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/advanced-analysis',
        component: () => import('@/pages/app/advanced-analysis/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/advanced-analysis-config',
        component: () => import('@/pages/app/advanced-analysis-config/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/data-viewer',
        component: () => import('@/pages/app/data-viewer/page.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/app/legacy-viewer',
        component: () => import('@/pages/app/legacy-viewer/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin] }
      },
      {
        path: '/app/migration-management',
        component: () => import('@/pages/app/migration-management/page.vue'),
        meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin] }
      },
      {
        path: "/app/administration",
        component: AdminMasterLayout,
        meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper, UserRoles.TenantAdmin] },
        children: [
          { path: "", component: AdminDashboard, meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper, UserRoles.TenantAdmin] } },

          { path: "tenant-metrics", component: AdminTenantUsagePage, meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper, UserRoles.TenantAdmin] } },
          { path: "tenant-management", component: TenantAdministrationPage, meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper, UserRoles.TenantAdmin] } },
          {
            path: "warm-up",
            component: () => import('@/pages/app/administration/warm-up/page.vue'),
            meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper] }
          },
          {
            path: "audit-logs",
            component: () => import('@/pages/app/administration/audit-logs/page.vue'),
            meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformAuditor, UserRoles.TenantAdmin, UserRoles.TenantAuditor] }
          },
          {
            path: "diagnostics",
            component: () => import('@/pages/app/administration/diagnostics/page.vue'),
            meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper] }
          },
          {
            path: 'tenant-management/:action/:tenantId?',
            component: () => import('@/pages/app/administration/tenant-management/tab-section/page.vue'),
            meta: { requiresAuth: true, roles: [UserRoles.PlatformAdmin, UserRoles.PlatformDeveloper, UserRoles.TenantAdmin] }
          }
        ],
      },
      {
        path: '/app/settings',
        component: () => import('@/pages/app/settings/layout.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: '/app/settings/profile',
            component: () => import('@/pages/app/settings/profile/page.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: '/app/settings/security',
            component: () => import('@/pages/app/settings/security/page.vue'),
            meta: { requiresAuth: true }
          },

          {
            path: '/app/settings/subscription',
            component: () => import('@/pages/app/settings/subscription/page.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: '/app/settings/api',
            component: () => import('@/pages/app/settings/api/page.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: '/app/settings/templates',
            component: () => import('@/pages/app/settings/templates/page.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: '/app/settings/configuration',
            component: () => import('@/pages/app/settings/configuration/page.vue'),
            meta: { requiresAuth: true, roles: [UserRoles.PlatformDeveloper, UserRoles.PlatformAdmin] }
          },
          {
            path: '/app/settings/token',
            component: () => import('@/pages/app/settings/token/page.vue'),
            meta: { requiresAuth: true }
          }
        ]
      }
    ]
  },
  {
    path: '/',
    component: () => import('@/pages/password-help/layout.vue'),
    children: [
      {
        path: '/forgot-password',
        component: () => import('@/pages/password-help/forgot-password/page.vue')
      },
      {
        path: '/reset-password',
        component: () => import('@/pages/password-help/reset-password/page.vue')
      },

    ]
  }
];

const router = createRouter({
  routes,
  history: createWebHistory()
});


//@ts-ignore from unused
router.beforeEach(async (to, from, next) => {
  try {
    const user = useUser();
    await user.initialize();
    const loggedIn = await isLoggedIn();

    // Check if this route requires authentication
    if (to.meta.requiresAuth && !loggedIn) {
      return next({ path: '/', query: { redirect: to.fullPath } })
    }

    // Token claim validation for authenticated users
    if (loggedIn && to.path !== '/') {
      const claimResult = validateTokenClaims(user.user)

      if (!claimResult.isValid && to.path !== '/app/lockout') {
        return next({ path: '/app/lockout' })
      }

      if (claimResult.isValid && to.path === '/app/lockout') {
        return next({ path: '/app' })
      }
    }

    // Optional: check roles
    if (Array.isArray(to?.meta?.roles) &&
      to.meta.roles.length > 0 &&
      (!Array.isArray(user.user.roles) ||
        !user.user.roles.some((role: string) => (to.meta.roles as string[]).includes(role)))) {
      return next({ path: '/app' })
    }

    if (to.path === '/' && loggedIn) {
      const redirect = router.currentRoute.value.query.redirect?.toString()
      return next({ path: redirect ?? '/app' })
    }

    next()
  } catch (error) {
    console.error('Navigation guard error:', error)
    // Allow navigation to proceed even if auth/user initialization fails
    // so pages aren't silently blocked by transient API errors
    next()
  }
})

// reload the app when we detect module loading issues
// this often happens after a new deployment and we've wiped
// the cache of the older version from the CDN (CloudFront)
router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      try {
        window.location.href = to.fullPath;
      } catch (err) {
        console.error("error trying to reload", err, to)
      }

    }
  } else {

    console.warn("error trying to route somewhere", error, to)
  }
});

export default router;
